import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';
import './Contact.css';

const Contact = () => {
  const [searchParams] = useSearchParams();
  const honeypotRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  // Load reCAPTCHA v3 script on component mount
  useEffect(() => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY;
    
    if (!siteKey) {
      console.warn('reCAPTCHA v3 site key niet gevonden in .env');
      return;
    }

    // Check if script is already loaded
    if (window.grecaptcha) {
      window.grecaptcha.ready(() => {
        setRecaptchaLoaded(true);
      });
      return;
    }

    // Load reCAPTCHA v3 script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.grecaptcha.ready(() => {
        setRecaptchaLoaded(true);
      });
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup: remove script if component unmounts (optional)
      const existingScript = document.querySelector(`script[src*="recaptcha/api.js"]`);
      if (existingScript && existingScript.parentNode) {
        existingScript.parentNode.removeChild(existingScript);
      }
    };
  }, []);

  // Set the service from URL parameter on component mount
  useEffect(() => {
    const serviceFromUrl = searchParams.get('service');
    if (serviceFromUrl) {
      // Map service names from Services page to Contact page dropdown options
      const serviceMapping = {
        'Kennismakingsgesprek': 'Kennismakingsgesprek',
        'Kickstart traject': 'Kickstart traject',
      };
      
      const mappedService = serviceMapping[serviceFromUrl] || serviceFromUrl;
      setFormData(prev => ({
        ...prev,
        service: mappedService
      }));
    }
  }, [searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Validate form data
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Naam is verplicht';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Naam moet minimaal 2 karakters bevatten';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'E-mail is verplicht';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Voer een geldig e-mailadres in';
    }
    
    if (!formData.message.trim()) {
      errors.message = 'Bericht is verplicht';
    } else if (formData.message.trim().length < 10) {
      errors.message = 'Bericht moet minimaal 10 karakters bevatten';
    }
    
    // Check honeypot field
    if (honeypotRef.current && honeypotRef.current.value) {
      errors.honeypot = 'Bot detected';
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent rapid submissions
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setIsSubmitting(false);
      return;
    }

    // Check if reCAPTCHA is loaded
    if (!recaptchaLoaded || !window.grecaptcha) {
      setFormErrors({ 
        submit: 'Beveiligingscheck nog niet geladen. Wacht even en probeer het opnieuw.' 
      });
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Get reCAPTCHA v3 token
      const siteKey = process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY;
      if (!siteKey) {
        throw new Error('reCAPTCHA configuratie ontbreekt. Controleer de .env file.');
      }

      const recaptchaToken = await window.grecaptcha.execute(siteKey, { action: 'submit' });

      // In development: proxy in package.json stuurt /api/contact door naar localhost:3001
      // In production: zet REACT_APP_CONTACT_API_URL naar de volledige API-URL (bijv. https://api.cthenature.com/api/contact)
      const apiUrl = process.env.REACT_APP_CONTACT_API_URL || '/api/contact';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || '',
          service: formData.service || '',
          message: formData.message,
          recaptcha_token: recaptchaToken,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || `Fout bij versturen (${response.status})`);
      }

      if (!data.success) {
        throw new Error(data.error || 'Er is een fout opgetreden.');
      }

      // Success - show success message
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
      setFormErrors({});
      
      // Reset the submitted state after 5 seconds (longer to read success message)
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      console.error('Form submission failed:', error);
      setFormErrors({ 
        submit: error.message?.includes('configuratie') || error.message?.includes('ontbreekt')
          ? 'Server configuratie fout. Neem contact op met de beheerder.' 
          : (error.message || 'Er is een fout opgetreden bij het versturen. Controleer je verbinding en probeer het later opnieuw.')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
               {
             icon: <Mail size={24} />,
             title: "E-mail",
             details: "cthenature@outlook.com",
             link: "mailto:cthenature@outlook.com"
           },
    {
      icon: <MapPin size={24} />,
      title: "Locatie",
      details: "Deursen-Dennenburg, omgeving Ravenstein.",
      link: null
    },
    {
      icon: <Clock size={24} />,
      title: "Beschikbaarheid",
      details: "Op afspraak",
      link: null
    }
  ];

  const services = [
    "Kennismakingsgesprek",
    "Kickstart traject",
    "Duurzaam traject"
  ];

  return (
    <div className="contact-page">
      <SEO
        title="Contact - Wandelcoaching Kennismakingsgesprek Aanvragen | CtheNature"
        description="Neem contact op voor een gratis kennismakingsgesprek wandelcoaching. Wandelcoach Chantal helpt je met persoonlijke ontwikkeling, stressmanagement en balans. Contactformulier of email: cthenature@outlook.com. Omgeving Ravenstein."
        keywords="wandelcoaching contact, coaching contact, kennismakingsgesprek aanvragen, wandelcoach contact, coaching omgeving Ravenstein, contact wandelcoaching, gratis kennismaking coaching, wandelcoaching aanmelden"
        url="/contact"
      />
                   {/* Hero Section */}
             <section className="contact-hero">
               <div className="contact-hero-background">
                 <img 
                   //src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
                   src="/berg1.jpg"
                   alt="Contact wandelcoaching CtheNature - Natuurlijke omgeving voor coaching sessies" 
                   className="contact-hero-background-img"
                 />
                 <div className="contact-hero-overlay"></div>
               </div>
               <div className="container">
          <motion.div 
            className="contact-hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="contact-hero-title">Contact</h1>
            <p className="contact-hero-subtitle">
              Neem contact op voor een gratis kennismakingsgesprek en ontdek hoe 
              wandelcoaching jou kan helpen groeien.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section contact-section">
        <div className="container">
          <div className="contact-content">
            {/* Contact Form */}
            <motion.div 
              className="contact-form-container"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="contact-form-title">Stuur mij een bericht</h2>
              <p className="contact-form-subtitle">
                Vul het formulier in en ik neem binnen 24 uur contact met je op.
              </p>

              {isSubmitted ? (
                <motion.div 
                  className="success-message"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <CheckCircle size={48} />
                  <h3>Bedankt voor je bericht!</h3>
                  <p>Ik neem binnen 24 uur contact met je op.</p>
                </motion.div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Naam *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="Jouw naam"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">E-mail *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="jouw@email.nl"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">Telefoon</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+31 6 12345678"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="service">Gewenste Dienst</label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                      >
                        <option value="">Selecteer een dienst</option>
                        {services.map((service, index) => (
                          <option key={index} value={service}>
                            {service}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Bericht *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows="5"
                        placeholder={'Vertel iets over jouw situatie. Weet je het nog niet precies? Dat is helemaal oké.\n\nBijvoorbeeld:\n- Waar loop je momenteel tegenaan?\n- Wat zou je graag anders willen?\n- Wat hoop je te ontdekken of te bereiken?'}
                    />
                    {formErrors.message && (
                      <span className="error-message">{formErrors.message}</span>
                    )}
                  </div>

                  {/* Honeypot field - hidden from users but visible to bots */}
                  <div className="honeypot-field" style={{ display: 'none' }}>
                    <input
                      ref={honeypotRef}
                      type="text"
                      name="website"
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  {/* reCAPTCHA v3 - werkt op de achtergrond, geen zichtbare UI */}
                  {!recaptchaLoaded && (
                    <div className="recaptcha-loading">
                      <small>Beveiligingscheck wordt geladen...</small>
                    </div>
                  )}

                  {formErrors.submit && (
                    <div className="error-message submit-error">{formErrors.submit}</div>
                  )}

                  <button 
                    type="submit" 
                    className="btn btn-primary submit-btn"
                    disabled={isSubmitting}
                  >
                    <Send size={20} />
                    {isSubmitting ? 'Bezig met versturen...' : 'Verstuur Bericht'}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Contact Info */}
            <motion.div 
              className="contact-info-container"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="contact-info-title">Contact Informatie</h2>
              <p className="contact-info-subtitle">
                Neem gerust contact op via onderstaand mailadres of via het contactformulier.
              </p>

              <div className="contact-info-list">
                {contactInfo.map((info, index) => (
                  <motion.div 
                    key={index}
                    className="contact-info-item"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="contact-info-icon">
                      {info.icon}
                    </div>
                    <div className="contact-info-content">
                      <h3 className="contact-info-title-small">{info.title}</h3>
                      {info.link ? (
                        <a href={info.link} className="contact-info-link">
                          {info.details}
                        </a>
                      ) : (
                        <p className="contact-info-details">{info.details}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="contact-note">
                <h3>Gratis Kennismaking</h3>
                <p>
                  Ik bied een gratis kennismakingsgesprek aan om te bespreken hoe 
                  wandelcoaching jou kan helpen. Dit gesprek duurt ongeveer 30 minuten 
                  en kan digitaal of wandelend plaatsvinden.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section map-section">
        <div className="container">
          <motion.div 
            className="map-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Waar ik actief ben</h2>
            <p className="section-subtitle">
            
            </p>
            <div className="map-placeholder">
              <div className="map-content-inner">
                <MapPin size={48} />
                <p>De wandelcoaching vindt plaats in:</p>
                <span>Deursen-Dennenburg, Ravenstein, natuurgebied Herperduin of een locatie naar keuze (in overleg, kleine vergoeding) </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
